import { apiJson } from '@/api/client'
import type {
  BulkMarkReadPayload,
  Notification,
  NotificationPreferences,
  NotificationsListFilters,
  NotificationsListResponse,
  PreferencesResponse,
  SetDndPayload,
  UnreadCountResponse,
} from './types'

function paramsFor(f: NotificationsListFilters = {}): string {
  const sp = new URLSearchParams()
  for (const k of f.kind ?? []) sp.append('kind', k)
  if (f.read_state) sp.set('read_state', f.read_state)
  if (f.cursor) sp.set('cursor', f.cursor)
  if (f.limit) sp.set('limit', String(f.limit))
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export function listNotifications(filters: NotificationsListFilters = {}) {
  return apiJson<NotificationsListResponse>(`/notifications${paramsFor(filters)}`)
}

export function getUnreadCount() {
  return apiJson<UnreadCountResponse>(`/notifications/unread-count`)
}

export function markRead(id: string) {
  return apiJson<{ notification: Notification }>(
    `/notifications/${id}/read`,
    { method: 'POST' },
  )
}

export function markAllRead() {
  return apiJson<{ marked: number }>(`/notifications/mark-all-read`, {
    method: 'POST',
  })
}

export function bulkMarkRead(payload: BulkMarkReadPayload) {
  return apiJson<{ marked: number }>(`/notifications/bulk-mark-read`, {
    method: 'POST',
    body: payload,
  })
}

export function getPreferences() {
  return apiJson<PreferencesResponse>(`/notifications/preferences`)
}

export function updatePreferences(prefs: NotificationPreferences) {
  return apiJson<PreferencesResponse>(`/notifications/preferences`, {
    method: 'PUT',
    body: { preferences: prefs },
  })
}

export function setDnd(payload: SetDndPayload) {
  return apiJson<PreferencesResponse>(`/notifications/dnd`, {
    method: 'POST',
    body: payload,
  })
}

export function clearDnd() {
  return apiJson<PreferencesResponse>(`/notifications/dnd`, { method: 'DELETE' })
}

// ===== Slide #33 — Customer Communication Workflows =====
//
// Catalogue CRUD over notifications.templates plus a delivery-audit
// feed reader for the right-rail panel. Backed by
// internal/api/notification_templates_handler.go.

export type NotificationChannelTemplate =
  | 'sms'
  | 'email'
  | 'push'
  | 'in_app'
  | 'webhook'

export interface NotificationTemplate {
  id: string
  name: string
  display_name: string
  description?: string
  channels: NotificationChannelTemplate[]
  subject_template?: string
  body_template: string
  required_vars: string[]
  status: 'draft' | 'active' | 'archived'
  is_active: boolean
  owner_team: string
  created_at: string
  updated_at: string
}

export interface NotificationTemplateBody {
  name: string
  display_name: string
  description?: string
  channels: NotificationChannelTemplate[]
  subject_template?: string
  body_template: string
  required_vars: string[]
  status?: 'draft' | 'active' | 'archived'
  is_active: boolean
  owner_team?: string
}

export interface NotificationDelivery {
  id: string
  template_name: string
  channel: string
  recipient_type: string
  recipient_value: string
  outcome: string
  external_message_id?: string
  error_message?: string
  queued_at: string
  dispatched_at?: string | null
  delivered_at?: string | null
}

// Detail variant of NotificationDelivery — superset that carries
// the rendered subject + body. Returned by GET /notification-deliveries/{id}.
export interface NotificationDeliveryDetail extends NotificationDelivery {
  rendered_subject?: string
  rendered_body: string
}

export function getNotificationDelivery(id: string) {
  return apiJson<NotificationDeliveryDetail>(
    `/notification-deliveries/${encodeURIComponent(id)}`,
  )
}

export function listNotificationTemplates() {
  return apiJson<{ templates: NotificationTemplate[] }>('/notification-templates')
}

export function createNotificationTemplate(body: NotificationTemplateBody) {
  return apiJson<NotificationTemplate>('/notification-templates', {
    method: 'POST',
    body,
  })
}

export function updateNotificationTemplate(
  id: string,
  body: NotificationTemplateBody,
) {
  return apiJson<NotificationTemplate>(`/notification-templates/${id}`, {
    method: 'PUT',
    body,
  })
}

export function setNotificationTemplateActive(id: string, active: boolean) {
  return apiJson<void>(`/notification-templates/${id}/active`, {
    method: 'PATCH',
    body: { is_active: active },
  })
}

export function deleteNotificationTemplate(id: string) {
  return apiJson<void>(`/notification-templates/${id}`, { method: 'DELETE' })
}

export function listNotificationDeliveries(limit = 10) {
  return apiJson<{ deliveries: NotificationDelivery[] }>(
    `/notification-deliveries?limit=${limit}`,
  )
}
